<template>
	<div @click="handleUserInteraction" @touchstart="handleUserInteraction" ref="messagesList">
		<header-component type="home" />
		<p class="thread-title">{{ titleThread }}</p>
		<welcome-message v-if="secretMode" variant="secret" />
		<welcome-message v-if="fileAvailable && !imageEditMode" variant="attached" />
		<welcome-message v-if="showWelcomeImageEditMessage" variant="imageEdit" />
		<welcome-message v-if="showWelcomeSearchMessage" variant="search" />
		<welcome-message v-if="showWelcomeMusicMessage" variant="music" />
		<welcome-message v-if="showWelcomeSongMessage" variant="song" />
		<welcome-message v-if="showWelcomeMessage" variant="default" />
		<div v-for="m in messages" :key="m.id" class="thread-wrap-message">
			<message-reasoning v-if="m.reasoning_body" :message="m.reasoning_body" :showMessageDefault="true" />
			<img
				v-if="(m.source_image || m.image) && !m.is_ai_model"
				:src="m.source_image?.url ?? m.image?.url"
				class="thread-user-image"
				alt=""
			/>
			<message-component
				v-if="m.body && !m.music_tracks && (!imageThread || (!m.is_ai_model && imageThread))"
				:id="m.id"
				:text="m.body"
				:created-at="m.created_at"
				:is-ai-model="m.is_ai_model"
				:song-settings="getSongSettingsForMessage(m)"
			/>
			<attached-file
				v-if="m.file && m.file.name && !imageEditMode"
				:name="m.file.name"
				:mimeType="m.file.mime_type"
				:size="m.file.size_bytes"
				:showRemoveFile="false"
				class="thread-attached-file"
			/>
			<message-image v-if="m.image && m.is_ai_model" :created-at="m.created_at" :imageUrl="m.image.url" />
			<message-music v-if="m.music_tracks" :tracks="m.music_tracks" :created-at="m.created_at" />
		</div>
		<message-error :message="errorMessage" v-if="errorMessage" :show-retry="errorShowRetry" @retry="handleRetry" />

		<div class="thread-loader-wrap" v-if="isLoadingState">
			<loader-message-component />
			<p class="thread-loader-text" v-if="isImageGeneration">{{ imageLoaderText }}</p>
		</div>
		<placeholder-img v-if="isImageGeneration" />
		<text-input
			v-if="!((songTopic || musicTopic) && messages.length > 0)"
			@send-message="sendMessage"
			@send-song-settings="handleSendSongSettings"
			:file-available="true"
			:placeholder-text="placeholderText"
			:disabled="isMusicGenerated || isSongGenerated || ((songTopic || musicTopic) && busy)"
			ref="TextInputRef"
		/>
		<image-example v-if="showImageExamples" />
	</div>
</template>

<script setup lang="ts">
import { onMounted, computed, watch, nextTick, useTemplateRef, shallowRef } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { useThreadStore } from '@/stores/thread'
import { useGenerationStore } from '@/stores/generation'
import { useComposerStore } from '@/stores/composer'
import { useCommonStore } from '@/stores/common'
import HeaderComponent from '@/components/HeaderComponent.vue'
import WelcomeMessage from '@/components/WelcomeMessage.vue'
import MessageError from '@/components/MessageError.vue'
import MessageComponent from '@/components/MessageComponent.vue'
import MessageImage from '@/components/MessageImage.vue'
import MessageMusic from '@/components/MessageMusic.vue'
import TextInput from '@/components/TextInput.vue'
import LoaderMessageComponent from '@/components/LoaderMessageComponent.vue'
import ImageExample from '@/components/ImageExample.vue'
import AttachedFile from '@/components/AttachedFile.vue'
import MessageReasoning from '@/components/ui/MessageReasoning.vue'
import PlaceholderImg from '@/components/ui/PlaceholderImg.vue'
import { type SongSettingsValue } from '@/components/ui/SongSettings.vue'
import { parseSongStyle } from '@/utils/songStyle'
import { findTopicById } from '@/utils/topics'
import { useAutoScroll } from '@/composables/useAutoScroll'
import { useGenerationResume } from '@/composables/useGenerationResume'
import { useFeatureGate } from '@/composables/useFeatureGate'
import type { UsageResource } from '@/stores/usage'
import type { TopicType } from '@/types'
type GenTopic = Extract<TopicType, 'image' | 'image_edit' | 'music' | 'song'>

const GEN_HANDLERS: Record<GenTopic, (id: number, message: string) => void> = {
	image: (id, message) => {
		generationStore.start(id, 'image', message, { imageHash: generationStore.getLastImageHash(id) })
	},
	image_edit: (id, message) => {
		const hasFile = !!composerStore.currentFile.document
		generationStore.start(id, 'image_edit', message, {
			imageHash: hasFile ? undefined : generationStore.getLastImageHash(id)
		})
	},
	music: (id, message) => {
		generationStore.start(id, 'music', message)
	},
	song: (id, message) => {
		const s = songSettingsSnapshot.value
		const style = s ? [...s.moods, ...s.genres, s.vocal, s.tempo].join(', ') : ''
		const vocal_gender = s?.vocal === 'female vocal' ? 'f' : 'm'
		generationStore.start(id, 'song', message, { style, vocal_gender })
	}
}

declare const window: Window
declare const document: Document

const route = useRoute()
const router = useRouter()
const threadStore = useThreadStore()
const generationStore = useGenerationStore()
const composerStore = useComposerStore()
const commonStore = useCommonStore()
const { checkResource, runResourceGate } = useFeatureGate()

const { handleUserInteraction, scrollToBottom } = useAutoScroll()
const { resumeIfPending, restorePromptFromTask } = useGenerationResume(() => threadId.value)

const sendingMessage = computed(() => commonStore.sendingMessage)
const topicsArray = computed(() => commonStore.topicsArray)
const messages = computed(() => threadStore.thread?.messages || [])
const threadId = computed(() => threadStore.thread?.id)
const secretChat = computed(() => threadStore.thread?.is_secret)
const searchTopic = computed(() => threadStore.thread?.topic?.type === 'search')
const musicTopic = computed(() => threadStore.thread.topic?.type === 'music')
const songTopic = computed(() => threadStore.thread.topic?.type === 'song')
const topicName = computed(() => threadStore.topicName)
const threadName = computed(() => threadStore.threadName || route.meta.title)
const isStreaming = computed(() => threadStore.isStreaming)
const fetchThread = computed(() => threadStore.fetchThread)
const fileAvailable = computed(() => threadStore.fileAvailable)
const imageEditMode = computed(() => threadStore.imageEditMode)
const showWelcomeImageEditMessage = computed(() => imageEditMode.value && !messages.value.length && !busy.value)
const errorMessage = computed(() => threadStore.errorMessage)
const errorShowRetry = computed(() => threadStore.errorShowRetry)
const secretMode = computed(() => threadStore.secretMode)
const imageThread = computed(() => threadStore.imgMode)
// Генерация текущего треда идёт (по реестру задач). Не завязано на глобальный sendingMessage,
// поэтому корректно при навигации между тредами.
const threadBusy = computed(() => generationStore.isBusy(threadId.value))
// "занят" = текстовый стрим (sendingMessage) ИЛИ фоновая генерация текущего треда
const busy = computed(() => sendingMessage.value || threadBusy.value)
const isLoadingState = computed(
	() =>
		(sendingMessage.value &&
			(!isStreaming.value || (!threadStore.messageStream && !threadStore.reasoningMessageStream))) ||
		generationStore.isBusy(threadId.value)
)
// Скелетон изображения и подпись — ровно на время активной задачи генерации (creating|pending),
// включая самый первый запрос в треде и восстановление после F5. Завязка на реестр задач, а не на
// isLoadingState: последний истинен и при создании треда (fetchThread → sendingMessage в onMounted),
// когда генерации ещё нет и скелетон показывать нельзя.
const isImageGeneration = computed(() => (imageThread.value || imageEditMode.value) && threadBusy.value)
const imageLoaderText = computed(() => (imageEditMode.value ? 'Редактирование изображения' : 'Генерация изображения'))
const awaitMessage = computed(() => threadStore.awaitMessage)
const prevThreadId = computed(() => threadStore.prevThreadId)
const showWelcomeSearchMessage = computed(() => !messages.value.length && !busy.value && searchTopic.value)
const showWelcomeMessage = computed(
	() => !messages.value.length && !busy.value && !route.params.id && !secretMode.value && !imageThread.value
)
const showWelcomeMusicMessage = computed(() => !messages.value.length && !busy.value && musicTopic.value)
// Примеры — только пустое состояние image-треда: после первого промпта (в т.ч. при ошибке
// генерации или после F5, когда сервер ещё не сохранил сообщение) они возвращаться не должны.
const showImageExamples = computed(() => imageThread.value && !messages.value.length && !busy.value)
const isMusicGenerated = computed(() => {
	return musicTopic.value && messages.value.some((m) => m.music_tracks?.length)
})
const isSongGenerated = computed(() => {
	return songTopic.value && messages.value.some((m) => m.music_tracks?.length)
})
const showWelcomeSongMessage = computed(() => !messages.value.length && !busy.value && songTopic.value)
const titleThread = computed(() => {
	if (secretMode.value) {
		return 'Секретный чат'
	}
	if (searchTopic.value) {
		return 'Поиск в интернете'
	}

	if (musicTopic.value) {
		return 'Генерация музыки'
	}

	if (songTopic.value) {
		return 'Генерация песни'
	}

	if (imageEditMode.value) {
		return 'Редактирование изображения'
	}

	return threadName.value
})

const placeholderText = computed(() => {
	if (imageEditMode.value) {
		return 'Опишите, что изменить в изображении'
	}
	if (imageThread.value) {
		return 'Опишите изображение для генерации'
	}
	if (musicTopic.value) {
		if (isMusicGenerated.value) {
			return 'Повторная генерация в рамках этого чата недоступна'
		}
		return 'Опишите музыку, которую хотите сгенерировать'
	}

	if (songTopic.value) {
		if (isSongGenerated.value) {
			return 'Повторная генерация в рамках этого чата недоступна'
		}
		return 'Напишите текст для AI-генерации и выберите необходимые настройки'
	}

	return undefined
})
const codeInputRef = useTemplateRef('TextInputRef')
const songSettingsSnapshot = shallowRef<SongSettingsValue | null>(null)

function handleSendSongSettings(value: SongSettingsValue) {
	songSettingsSnapshot.value = value
}

function getSongSettingsForMessage(m: (typeof messages.value)[number]): SongSettingsValue | null {
	if (m.is_ai_model || !songTopic.value) return null
	const styleStr = threadStore.thread.meta?.song_style
	if (styleStr) return parseSongStyle(styleStr)
	return songSettingsSnapshot.value
}

function resourceForType(type: TopicType | null): UsageResource {
	if (type === 'image' || type === 'image_edit') return 'image'
	if (type === 'music' || type === 'song') return 'song'
	return 'text'
}

async function sendMessage(message: string) {
	const resource = resourceForType(threadStore.currentTopicType as TopicType | null)
	if (!runResourceGate(checkResource(resource), resource)) return

	await threadStore.createMockMessage(message)
	scrollToBottom()

	const id = threadId.value
	const type = threadStore.currentTopicType as TopicType | null

	if (id !== undefined && type && type in GEN_HANDLERS) {
		GEN_HANDLERS[type as GenTopic](id, message)
		return
	}

	commonStore.enableSendingMessage()
	if (id !== undefined) await threadStore.createMessage(message)
	else await threadStore.createThread({ promt: message })
}

// Перезапуск генерации по уже существующему сообщению пользователя: в отличие от sendMessage
// не пушит второй бабл с промптом (в music/song инпут скрыт, и «Повторить» — единственный путь).
function retryGeneration(message: string): boolean {
	const id = threadId.value
	const type = threadStore.currentTopicType as TopicType | null
	if (id === undefined || !type || !(type in GEN_HANDLERS)) return false
	// Повтор — та же генерация, и гейт ей нужен тот же: раньше он здесь не проходил вовсе.
	const resource = resourceForType(type)
	if (!runResourceGate(checkResource(resource), resource)) return true
	// Промпт должен остаться видимым: если сервер его ещё не сохранил и бабла в треде нет,
	// отдаём перезапуск sendMessage — он добавит сообщение пользователя
	const promptVisible = messages.value.some((m) => !m.is_ai_model && m.body?.trim() === message.trim())
	if (!promptVisible) return false
	GEN_HANDLERS[type as GenTopic](id, message)
	return true
}

async function handleRetry() {
	const lastUserMessage = messages.value.filter((m) => !m.is_ai_model && m.body).pop()
	if (!lastUserMessage?.body) return

	threadStore.clearErrorMessage()
	if (threadId.value && generationStore.tasks.has(threadId.value)) {
		// Есть незавершённая задача — возобновляем (poll по taskId или refetch треда)
		generationStore.resume(threadId.value)
	} else if (songTopic.value && threadId.value) {
		// Задачи нет — возможно песня уже сгенерирована на бэкенде
		await threadStore.getThread(threadId.value)
		if (!isSongGenerated.value) {
			if (!retryGeneration(lastUserMessage.body)) sendMessage(lastUserMessage.body)
		}
	} else if (!retryGeneration(lastUserMessage.body)) {
		sendMessage(lastUserMessage.body)
	}
}

// Скелетон (452px) появляется тиком позже промпта — задача попадает в реестр уже после того,
// как отработал автоскролл на изменение списка сообщений. Доскроливаем, когда он отрисован.
watch(isImageGeneration, async (show) => {
	if (!show) return
	await nextTick()
	scrollToBottom()
})

watch(threadId, (newId) => {
	if (newId && !secretMode.value) {
		threadStore.setPrevThreadId(newId)
		router.replace(`/thread/${newId}`)
	}
})

onMounted(async () => {
	if (fetchThread.value) {
		commonStore.enableLoading()
		commonStore.enableSendingMessage()
	}
	if (!topicsArray.value || topicsArray.value.length === 0) {
		await commonStore.fetchTopics()
	}
	if (route.params.id) {
		const idNumber = Number(route.params.id)
		if (idNumber === prevThreadId.value && threadId.value) {
			// Тот же тред в этой сессии: awaitMessage — про текстовый стрим; генерацию отражает busy
			if (awaitMessage.value) {
				commonStore.enableSendingMessage()
			}
		} else {
			await threadStore.clearProccess()
			await threadStore.getThread(idNumber)
			if (secretChat.value) router.push('/')
			// Реконсиляция задачи генерации с только что полученным с сервера состоянием:
			// resume сам решит — продолжить poll music/song, либо дропнуть (image / уже готово).
			resumeIfPending(idNumber)
		}
		restorePromptFromTask(idNumber)
		// Поиск идёт по всему дереву тем (включая вложенные в группы) — иначе для темы
		// внутри группы тема не находилась и topicName/topicType не восстанавливались.
		const findTopic = findTopicById(topicsArray.value, threadStore.thread.topic?.id)
		threadStore.setPrevThreadId(idNumber)
		if (findTopic) {
			threadStore.setTopicName(findTopic.name)
			// Восстанавливаем режим треда из авторитетного типа темы (тег), т.к. getThread
			// может вернуть topic.type, по которому imageEditMode/imgMode не выставляются
			threadStore.setTopicType(findTopic.type)
		}
	}
	if (topicName.value) {
		document.title = topicName.value
	}
	window.scrollTo({
		top: document.body.scrollHeight
	})
	// await nextTick()
	// isInitialLoad.value = false
})

onBeforeRouteLeave((to) => {
	// Задача генерации остаётся в реестре по своему threadId — уход её НЕ отменяет
	if (!to.path.includes('thread/')) {
		threadStore.clearSecretMode()
		composerStore.clearReasoningMode()
		if (codeInputRef.value) {
			codeInputRef.value.removeFile()
		}
	}
})
</script>

<style lang="scss" scoped>
.thread {
	&-title {
		font: var(--font-heading);
		text-align: center;
		color: var(--text-primary);
		margin-bottom: 14px;
		min-height: 24px;
	}
	&-attached-file {
		margin-right: 0;
		margin-left: auto;
	}
	&-user-image {
		max-width: 240px;
		max-height: 240px;
		object-fit: cover;
		border-radius: var(--radius-lg);
		margin-bottom: 8px;
	}
	&-wrap-message {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
	}
	&-loader-wrap {
		@include flex-start;
	}
	&-loader-text {
		margin-left: 4px;
		font: var(--font-caption-large);
	}
}
</style>
